import { Autocomplete, Box, TextField } from '@mui/material';
import { DATE_PRESETS } from '../utils/date-presets';
import { SORTABLE_FIELDS, type TrackingDefaults } from '../utils/user-settings';
import LevelPicker from './LevelPicker';
import { TIMEZONE_OPTIONS } from '../utils/timezone';

/** "No preference" is an absent key, so the sentinel is a value none of the options use. */
const INHERIT = '';

interface Choice { value: string; label: string }

/**
 * One field: the options plus a leading "Default (…)" that names what it inherits, so an
 * unset field says what it will do rather than looking empty.
 */
function DefaultField({ label, options, defaultLabel, value, onPick }: {
  label: string;
  options: Choice[];
  defaultLabel: string;
  value: string;
  onPick: (v: string) => void;
}) {
  const all = [{ value: INHERIT, label: `Default (${defaultLabel})` }, ...options];
  return (
    <Autocomplete
      size="small"
      fullWidth
      disableClearable
      options={all}
      value={all.find((o) => o.value === value) ?? all[0]}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      getOptionLabel={(o) => o.label}
      onChange={(_, o) => onPick(o?.value ?? INHERIT)}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
}

/**
 * The report defaults, in one place. Mounted twice: a user editing their own under Settings,
 * and an admin editing someone else's from the user list. Same fields either way — only the
 * subject differs.
 */
export default function ReportDefaultsForm({ value, onChange, admin = false }: {
  value: TrackingDefaults;
  onChange: <K extends keyof TrackingDefaults>(key: K, v: TrackingDefaults[K] | undefined) => void;
  admin?: boolean;
}) {
  const row = { display: 'flex', gap: 2 } as const;
  // object/null → "no preference", not a String()-crash on a non-primitive pasted via the settings JSON
  const str = (v: unknown) => (v === undefined || typeof v === 'object' ? INHERIT : String(v));
  const bool = (key: 'exclude_bots' | 'conversions_by_click' | 'show_workspace_strip') => (v: string) =>
    onChange(key, v === INHERIT ? undefined : v === 'true');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={row}>
        <DefaultField
          label="Date range" defaultLabel="Last 7 days"
          options={DATE_PRESETS.map((p) => ({ value: p.key, label: p.title }))}
          value={str(value.date_preset)}
          onPick={(v) => onChange('date_preset', (v || undefined) as TrackingDefaults['date_preset'])}
        />
        <DefaultField
          label="Timezone" defaultLabel="UTC-7 · PDT"
          options={TIMEZONE_OPTIONS.map((tz) => ({ value: String(tz.value), label: tz.label }))}
          value={str(value.timezone)}
          // UTC is offset 0, so this must tell "" from a falsy offset.
          onPick={(v) => onChange('timezone', v === INHERIT ? undefined : Number(v))}
        />
      </Box>

      <Box sx={row}>
        <LevelPicker label="Grouping (default: Date)" level={str(value.group_by)} sx={{ flex: 1 }}
          onChange={(v) => onChange('group_by', v || undefined)} />
        <LevelPicker label="Drilldown (default: Traffic Sources)" level={str(value.drilldown_group_by)} sx={{ flex: 1 }}
          onChange={(v) => onChange('drilldown_group_by', v || undefined)} />
      </Box>

      <Box sx={row}>
        <LevelPicker label="Drilldown 2 (default: none)" level={str(value.drilldown_group_by_2)} sx={{ flex: 1 }}
          onChange={(v) => onChange('drilldown_group_by_2', v || undefined)} />
        <LevelPicker label="Drilldown 3 (default: none)" level={str(value.drilldown_group_by_3)} sx={{ flex: 1 }}
          onChange={(v) => onChange('drilldown_group_by_3', v || undefined)} />
      </Box>

      <Box sx={row}>
        <DefaultField
          label="Bot traffic" defaultLabel="excluded"
          options={[{ value: 'true', label: 'Exclude bots' }, { value: 'false', label: 'Include bots' }]}
          value={str(value.exclude_bots)}
          onPick={bool('exclude_bots')}
        />
        <DefaultField
          label="Conversions by" defaultLabel="conversion time"
          options={[{ value: 'false', label: 'Conversion time' }, { value: 'true', label: 'Click time' }]}
          value={str(value.conversions_by_click)}
          onPick={bool('conversions_by_click')}
        />
      </Box>

      <Box sx={row}>
        <DefaultField
          label="Sort by" defaultLabel="Visits"
          options={SORTABLE_FIELDS.map((f) => ({ value: f.value, label: f.label }))}
          value={SORTABLE_FIELDS.some((f) => f.value === value.order_by) ? String(value.order_by) : INHERIT}
          onPick={(v) => onChange('order_by', v || undefined)}
        />
        <DefaultField
          label="Direction" defaultLabel="Descending"
          options={[{ value: 'desc', label: 'Descending' }, { value: 'asc', label: 'Ascending' }]}
          value={str(value.order)}
          onPick={(v) => onChange('order', v === INHERIT ? undefined : (v as 'asc' | 'desc'))}
        />
      </Box>
      {admin && (
        <Box sx={row}>
          <DefaultField
            label="Workspace bar" defaultLabel="shown"
            options={[{ value: 'true', label: 'Show' }, { value: 'false', label: 'Hide' }]}
            value={str(value.show_workspace_strip)}
            onPick={bool('show_workspace_strip')}
          />
          <Box sx={{ flex: 1 }} />
        </Box>
      )}
    </Box>
  );
}
